'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const REFRESH_INTERVAL = 180; // 3 minutes in seconds

interface AutoRefreshBarProps {
  onRefresh: () => void;
  lastUpdated: string;
}

export default function AutoRefreshBar({ onRefresh, lastUpdated }: AutoRefreshBarProps) {
  const [secondsLeft, setSecondsLeft] = useState(REFRESH_INTERVAL);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const doRefresh = useCallback(() => {
    setIsRefreshing(true);
    setTimeout(() => {
      onRefresh();
      setIsRefreshing(false);
      setSecondsLeft(REFRESH_INTERVAL);
      toast.success('Token stats refreshed');
    }, 800);
  }, [onRefresh]);

  useEffect(() => {
    const interval = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          doRefresh();
          return REFRESH_INTERVAL;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [doRefresh]);

  const progress = ((REFRESH_INTERVAL - secondsLeft) / REFRESH_INTERVAL) * 100;
  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;

  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border border-zinc-800 bg-card px-5 py-3">
      <div className="flex items-center gap-3">
        <button
          onClick={doRefresh}
          disabled={isRefreshing}
          className="p-1.5 rounded-lg hover:bg-zinc-800 transition-colors disabled:opacity-50"
          aria-label="Refresh now"
        >
          <RefreshCw
            size={14}
            className={`text-btcgold-400 ${isRefreshing ? 'animate-spin' : ''}`}
          />
        </button>
        <div className="flex flex-col gap-0.5">
          <span className="text-xs font-medium text-white">
            {isRefreshing ? 'Refreshing stats...' : `Next refresh in ${minutes}:${String(seconds).padStart(2, '0')}`}
          </span>
          <span className="text-xs text-zinc-500">Last updated: {lastUpdated}</span>
        </div>
      </div>

      <div className="flex items-center gap-3 sm:min-w-[160px]">
        <div className="flex-1 bg-zinc-800 rounded-full h-1.5 overflow-hidden">
          <div
            className="h-full bg-btcgold-500 rounded-full transition-all duration-1000"
            style={{ width: `${progress}%` }}
          />
        </div>
        <span className="text-xs font-mono text-zinc-500 flex-shrink-0">{Math.round(progress)}%</span>
      </div>
    </div>
  );
}